import { useState } from "react";
import { Link } from "react-router-dom";
import {
  AppleLogo, GoogleLogo, CheckCircle, DeviceMobile, EnvelopeSimple,
  FlagBanner, Sparkle, Heart,
} from "@phosphor-icons/react";
import MarketingLayout from "@/components/MarketingLayout";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";

const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=app.nestled6035650c&pcampaignid=web_share";

export default function BetaTesting() {
  return (
    <MarketingLayout
      title="iOS waitlist — Nestled home organization app for iPhone"
      description="The Nestled iPhone app is on the way. Join the iOS waitlist and we'll email the moment it's live in the App Store."
    >
      <article className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-sage-400/40 bg-sage-400/10 text-sage-600 text-xs font-bold uppercase tracking-[0.18em] mb-5">
            <FlagBanner weight="duotone" size={14} /> iOS Waitlist
          </div>
          <h1 className="font-heading text-4xl sm:text-6xl text-stoneink-900 font-semibold tracking-tight">
            iPhone version — coming soon.
          </h1>
          <p className="mt-5 text-lg text-stoneink-700 leading-relaxed max-w-2xl mx-auto">
            Nestled is <strong>now live on Android</strong>, and iOS is next. Add your email below and
            we&apos;ll be in touch the moment the App Store version drops.
          </p>

          {/* Android is live callout */}
          <div className="mt-8 inline-flex flex-wrap items-center gap-3 rounded-2xl border border-sand-300 bg-white px-5 py-4 shadow-ambient text-left">
            <div className="h-10 w-10 rounded-xl bg-sage-400/20 text-sage-600 grid place-items-center shrink-0">
              <GoogleLogo weight="duotone" size={20} />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-sage-600">Android is live</div>
              <div className="text-stoneink-900 font-medium">Have an Android phone? Download Nestled now.</div>
            </div>
            <a
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ios-waitlist-android-cta"
              className="ml-auto h-10 px-4 rounded-xl bg-stoneink-900 hover:bg-stoneink-900/90 text-white text-sm font-semibold flex items-center gap-2"
            >
              <GoogleLogo weight="fill" size={16} /> Google Play
            </a>
          </div>
        </div>

        <IosWaitlistForm />

        {/* Timeline */}
        <section className="mt-16">
          <h2 className="font-heading text-3xl text-stoneink-900 font-semibold tracking-tight">
            Here&apos;s the plan
          </h2>
          <ol className="mt-6 space-y-4">
            {[
              ["Android is live", "You can download Nestled on Google Play right now — full feature set, same data model as web."],
              ["iOS build kicks off next", "Same team, same design language, same warm feel — reimagined for iPhone."],
              ["Public TestFlight", "Waitlist members get first invites into the closed TestFlight beta, usually a couple of weeks before public launch."],
              ["App Store launch", "When we ship, you&apos;ll be one of the first emails to hear about it — no need to check back."],
            ].map(([title, body], i) => (
              <li key={title} className="flex gap-4 rounded-2xl border border-sand-300 bg-white p-5">
                <div className="h-9 w-9 rounded-full bg-terracotta-500 text-white grid place-items-center font-heading font-semibold shrink-0">
                  {i + 1}
                </div>
                <div>
                  <h3 className="font-heading text-lg text-stoneink-900 font-medium">{title}</h3>
                  <p className="mt-1 text-stoneink-700 leading-relaxed text-sm" dangerouslySetInnerHTML={{ __html: body }} />
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* While you wait */}
        <section className="mt-16 rounded-3xl bg-stoneink-900 text-white p-8 sm:p-10 text-center shadow-hover">
          <Heart weight="duotone" size={32} className="mx-auto text-terracotta-500" />
          <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-semibold tracking-tight">
            While you wait
          </h2>
          <p className="mt-3 text-sand-200 leading-relaxed max-w-2xl mx-auto">
            You can already use Nestled from any browser on your iPhone — add items, print QR labels,
            share with your household. Your data will be waiting when the native app arrives.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/register"
              data-testid="ios-waitlist-signup-cta"
              className="inline-flex items-center justify-center h-11 px-6 rounded-xl bg-terracotta-600 hover:bg-terracotta-700 text-white font-medium transition-colors"
            >
              Start free on the web
            </Link>
            <Link
              to="/try-demo"
              className="inline-flex items-center justify-center h-11 px-6 rounded-xl border border-white/30 hover:border-white/60 hover:bg-white/5 text-white font-medium transition-colors"
            >
              Or try the demo first
            </Link>
          </div>
        </section>

        <p className="mt-12 text-center text-sm text-stoneink-500">
          Thanks for wanting to be first — we&apos;ll make it worth the wait. 🏡
        </p>
      </article>
    </MarketingLayout>
  );
}

function IosWaitlistForm() {
  const [form, setForm] = useState({ name: "", email: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name) return;
    setBusy(true);
    try {
      await api.post("/beta/signup", { ...form, platform: "ios" });
      setSent(true);
      toast.success("You're on the iOS waitlist — we'll email when it's live.");
    } catch (err) {
      toast.error(formatError(err) || "Could not sign up. Try again or email contact@nestledapp.io.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <section className="mt-12 rounded-3xl border-2 border-sage-400/40 bg-sage-400/10 p-8 sm:p-10 text-center shadow-ambient" data-testid="ios-waitlist-success">
        <div className="h-14 w-14 rounded-full bg-sage-500 text-white grid place-items-center mx-auto">
          <CheckCircle weight="duotone" size={30} />
        </div>
        <h2 className="mt-4 font-heading text-2xl sm:text-3xl text-stoneink-900 font-semibold tracking-tight">
          You&apos;re on the list 🎉
        </h2>
        <p className="mt-3 text-stoneink-700 leading-relaxed max-w-xl mx-auto">
          We&apos;ve saved a spot for <strong>{form.email}</strong>. When the iOS app is ready you&apos;ll get an
          email from <strong>contact@nestledapp.io</strong> with early access.
        </p>
        <p className="mt-4 text-sm text-stoneink-500">
          Didn&apos;t get a confirmation? Check spam, or <Link to="/contact" className="text-terracotta-600 hover:underline">ping us</Link>.
        </p>
      </section>
    );
  }

  return (
    <section className="mt-12 rounded-3xl border-2 border-terracotta-500 bg-terracotta-50 p-8 sm:p-10 shadow-hover" data-testid="ios-waitlist-form-section">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600 mb-2">
        <EnvelopeSimple weight="duotone" size={14} /> Join the iOS waitlist
      </div>
      <h2 className="font-heading text-2xl sm:text-3xl text-stoneink-900 font-semibold tracking-tight">
        Get the first email when iOS launches
      </h2>
      <p className="mt-2 text-stoneink-700 leading-relaxed">
        We&apos;ll only email you about the iOS launch. No spam, no partner offers, unsubscribe anytime.
      </p>

      <form onSubmit={submit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-stoneink-700 mb-1.5">Your name *</label>
          <input
            data-testid="ios-waitlist-name-input"
            required value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Jane Doe"
            className="w-full px-4 h-11 rounded-xl bg-white border border-sand-300 focus:border-terracotta-500 outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-stoneink-700 mb-1.5">Email *</label>
          <input
            data-testid="ios-waitlist-email-input"
            type="email" required value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="jane@icloud.com"
            className="w-full px-4 h-11 rounded-xl bg-white border border-sand-300 focus:border-terracotta-500 outline-none"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-stoneink-700 mb-1.5">
            What made you want to try it? <span className="text-stoneink-500 font-normal">(optional)</span>
          </label>
          <textarea
            data-testid="ios-waitlist-reason-input"
            rows={3} value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            placeholder="e.g. I just moved and need to track where everything went..."
            className="w-full px-4 py-3 rounded-xl bg-white border border-sand-300 focus:border-terracotta-500 outline-none"
          />
        </div>
        <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-3">
          <button
            type="submit" disabled={busy || !form.email || !form.name}
            data-testid="ios-waitlist-submit"
            className="h-12 px-6 rounded-xl bg-terracotta-600 hover:bg-terracotta-700 text-white font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <AppleLogo weight="fill" size={16} />
            {busy ? "Adding you…" : "Join the iOS waitlist"}
          </button>
          <p className="text-xs text-stoneink-500 leading-snug">
            We&apos;ll only email you about iOS launch. Unsubscribe any time.
          </p>
        </div>
      </form>
    </section>
  );
}
